// src/types/branding/BrandingTypes.ts
/**
 * @file Types for the clinic's public branding payload (S6, ClickUp 86d3w1g8u).
 * @module types/branding/BrandingTypes
 *
 * *** THE FRONTEND NEVER DECIDES WHETHER TO WHITELABEL. ***
 *
 * `whitelabel` is the SERVER's answer (decision D1): the public endpoint applies
 * `BrandingService::isEntitled()` itself and returns the "off" shape when the clinic
 * does not hold the plan. `hasFeature('whitelabel')` in the frontend is presentation
 * only — which settings tab to show — and must never be used to decide whether to
 * render a clinic's logo. Read what we are handed; render that.
 */

/**
 * The clinic branding payload, as served by `GET {VITE_HEALTHCARE_SERVER}/public/branding`.
 *
 * Every key is present in every state — the endpoint returns nulls rather than omitting
 * keys — but see {@link normalizeBranding}: nothing in this app trusts that at runtime.
 */
export interface Branding {
	/** SERVER-decided: is this clinic entitled AND configured to whitelabel? */
	whitelabel: boolean;
	display_name: string | null;
	logo_url: string | null;
	/** Optional dark-background variant (D13). Falls back to `logo_url` when unset. */
	logo_dark_url: string | null;
	favicon_url: string | null;
	primary_color: string | null;
	/** The co-branding attribution (D7) — always "Xoe Med" today. */
	powered_by: string;
}

/**
 * What the app renders when branding is unavailable, unentitled, or malformed.
 *
 * This is today's appearance exactly: no clinic logo, no clinic colour, Xoe Med
 * branding throughout. Every failure path resolves here, so "the auth service is
 * briefly unreachable" and "this clinic has no whitelabel plan" render identically.
 */
export const DEFAULT_BRANDING: Branding = {
	whitelabel: false,
	display_name: null,
	logo_url: null,
	logo_dark_url: null,
	favicon_url: null,
	primary_color: null,
	powered_by: 'Xoe Med',
};

/** A string that is actually present — `""` is treated as unset, not as a value. */
const str = (value: unknown): string | null =>
	typeof value === 'string' && value.trim() !== '' ? value : null;

/**
 * Merge an untrusted payload over {@link DEFAULT_BRANDING}.
 *
 * ⚠️ Deliberately NOT `{ ...DEFAULT_BRANDING, ...payload }`. A spread would happily
 * copy `logo_url: 123` or `powered_by: undefined` straight through and hand a
 * component something to call `.length` on. Every field is checked for its own type,
 * and anything unrecognised falls back to the default — so a future backend change,
 * a proxy error page, or an HTML 502 body can only ever produce the "off" shape.
 *
 * `whitelabel` fails CLOSED: a non-boolean means no whitelabel.
 */
export const normalizeBranding = (payload: unknown): Branding => {
	if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
		return DEFAULT_BRANDING;
	}

	const raw = payload as Record<string, unknown>;

	return {
		whitelabel: typeof raw.whitelabel === 'boolean' ? raw.whitelabel : DEFAULT_BRANDING.whitelabel,
		display_name: str(raw.display_name),
		logo_url: str(raw.logo_url),
		logo_dark_url: str(raw.logo_dark_url),
		favicon_url: str(raw.favicon_url),
		primary_color: str(raw.primary_color),
		powered_by: str(raw.powered_by) ?? DEFAULT_BRANDING.powered_by,
	};
};
